import { TabsPanelId, useSplitView } from '@/app/_common/split-view/split-view-context'
import { createTurnStepTab } from '@/app/_common/split-view/tabs/factories'
import {
  isTurnStepTab,
  isWorkflowTab,
  Tab,
  TabTypeTurnStep,
} from '@/app/_common/split-view/tabs/types'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { cn } from '@/lib/utils'
import { ChevronDown, SquareArrowOutUpRightIcon } from 'lucide-react'
import React from 'react'
import { LiveViewStatusBadge, type LiveViewStatus } from '../live-view-status-badge'
import { useTurnStepParsed, type parseTurnStep } from '../use-turn-parsed'
import { StepStatusIcon } from './step-status'

type PlanStepProps = Readonly<{
  isExpanded: boolean
  onToggleExpand: () => void
  renderStepUpdatesList: (step: ReturnType<typeof parseTurnStep>) => React.ReactNode
  workflowId: string
  turnId: string
  stepId: string
}>

export const PlanStep: React.FC<PlanStepProps> = ({
  workflowId,
  turnId,
  stepId,
  isExpanded,
  onToggleExpand,
  renderStepUpdatesList,
}) => {
  const [liveViewStatus, setLiveViewStatus] = React.useState<LiveViewStatus | null>(null)
  const {
    addTab,
    activeTabIdLeft,
    activeTabIdRight,
    tabsLeft,
    tabsRight,
    filterTabs,
    getTabPositionMetadata,
  } = useSplitView()

  const parsedStep = useTurnStepParsed({ turnId, stepId, workflowId })
  const { agentData, outputDisplayData } = parsedStep ?? {}

  const stepTitle = React.useMemo(() => {
    if (!outputDisplayData?.description) return agentData?.agentDisplayName
    if (!agentData?.agentDisplayName) return outputDisplayData.description
    return (
      <span className='text-left'>
        <span className='opacity-55'>
          {agentData.agentDisplayName}
          {': '}
        </span>
        {outputDisplayData.description}
      </span>
    )
  }, [outputDisplayData?.description, agentData?.agentDisplayName])

  const isStepTabActive = React.useMemo(() => {
    const predicate = (tab: Tab, activeTabId: string) =>
      tab.id === activeTabId &&
      isTurnStepTab(tab) &&
      tab.workflowId === workflowId &&
      tab.turnId === turnId &&
      tab.stepId === stepId

    const isRightActive = Boolean(
      activeTabIdRight && tabsRight.some((t) => predicate(t, activeTabIdRight)),
    )

    if (isRightActive) return true

    return Boolean(activeTabIdLeft && tabsLeft.some((t) => predicate(t, activeTabIdLeft)))
  }, [tabsRight, tabsLeft, turnId, stepId, activeTabIdRight, activeTabIdLeft, workflowId])

  const getTargetPanelId = () => {
    const position = getTabPositionMetadata({
      predicate: (t) => isWorkflowTab(t) && t.workflowId === workflowId,
    })
    return position?.panelId === TabsPanelId.Right ? TabsPanelId.Left : TabsPanelId.Right
  }

  const handleOpenUpdatesTab = () => {
    addTab({
      panelId: getTargetPanelId(),
      tab: createTurnStepTab({
        type: TabTypeTurnStep.StepUpdates,
        turnId,
        stepId,
        workflowId,
        title: agentData?.agentDisplayName
          ? `${agentData.agentDisplayName} Updates`
          : 'Step Updates',
      }),
    })
  }

  const handleOpenSessionTab = () => {
    addTab({
      panelId: getTargetPanelId(),
      tab: createTurnStepTab({
        type:
          liveViewStatus === 'recorded'
            ? TabTypeTurnStep.RecordedSession
            : TabTypeTurnStep.LiveSession,
        turnId,
        stepId,
        workflowId,
      }),
    })
  }

  React.useEffect(() => {
    if (outputDisplayData?.recordingUrl) {
      setLiveViewStatus('recorded')
      filterTabs({
        predicate: (t) =>
          t.type === TabTypeTurnStep.LiveSession &&
          t.turnId === turnId &&
          t.stepId === stepId &&
          t.workflowId === workflowId,
      })
    } else if (outputDisplayData?.isActionRequired) {
      setLiveViewStatus('actionRequired')
    } else if (outputDisplayData?.isPaused) {
      setLiveViewStatus('paused')
    } else if (outputDisplayData?.liveSessionView) {
      setLiveViewStatus('live')
    } else {
      setLiveViewStatus(null)
    }
  }, [
    outputDisplayData?.recordingUrl,
    outputDisplayData?.liveSessionView,
    outputDisplayData?.isPaused,
    outputDisplayData?.isActionRequired,
    turnId,
    stepId,
    workflowId,
    filterTabs,
  ])

  return (
    <div
      className={cn(
        'flex w-full flex-col rounded-lg p-2 transition-colors duration-100 ease-in-out hover:bg-neutral-50/85',
        isStepTabActive && 'bg-neutral-50/85',
      )}
    >
      <div className='flex w-full items-center justify-between gap-2'>
        <div className='flex items-center gap-2 text-sm font-light'>
          <StepStatusIcon status={parsedStep?.status} />
          {stepTitle}
          {Boolean(outputDisplayData?.tasks.length) && (
            <div className='flex items-center gap-0.5'>
              <TooltipIconButton
                size='auto'
                className='size-6 text-neutral-150'
                titleString={isExpanded ? 'Collapse step details' : 'Expand step details'}
                onClick={onToggleExpand}
              >
                <ChevronDown
                  className={cn(
                    'size-4 shrink-0 transition-transform ease-in-out',
                    isExpanded && 'rotate-180',
                  )}
                />
              </TooltipIconButton>
              <TooltipIconButton
                size='auto'
                className='size-6 text-neutral-150'
                titleString='Open updates in new tab'
                onClick={handleOpenUpdatesTab}
              >
                <SquareArrowOutUpRightIcon className='size-3.5 shrink-0' />
              </TooltipIconButton>
            </div>
          )}
        </div>
        {liveViewStatus && (
          <LiveViewStatusBadge status={liveViewStatus} onClick={handleOpenSessionTab} />
        )}
      </div>
      <div
        style={{ maxHeight: isExpanded ? 192 : 0 }}
        className={cn(
          'overflow-hidden transition-[max-height,opacity,margin-top] ease-in-out',
          isExpanded ? 'mt-2 opacity-100' : 'opacity-0',
        )}
      >
        {isExpanded && parsedStep && renderStepUpdatesList(parsedStep)}
      </div>
    </div>
  )
}
